<template>
    <div class="mead-button-group"
        role="group"
        :style="uiBackgroundVars"
        :class="[
            inline ? 'el--inline' : '',
            uiHasBackgroundVars ? 'el--bg' : ''
        ]">
        <ButtonV2 v-for="(item, index) in items"
            :key="uiItemKey(item, index)"
            class="el-button-group-item"
            :label="item.label || ''"
            :tooltip="item.tooltip || false"
            :qualifier="item.qualifier === undefined ? false : item.qualifier"
            :icon="item.icon || false"
            :iconRight="item.iconRight || false"
            :size="size"
            :color="uiIsSelected(item) ? color : defaultColor"
            :inline="inline"
            :disabled="disabled || item.disabled === true"
            :aria-pressed="uiIsSelected(item) ? 'true' : 'false'"
            @click="onItemClick(item)">
            <slot :item="item" :selected="uiIsSelected(item)"></slot>
        </ButtonV2>
    </div>
</template>

<script>
import ButtonV2 from './ButtonV2.vue';

export default {
    name: 'mead-button-group',
    components: {ButtonV2},
    props: {
        /**
         * Buttons in the group. Each item is an object with a `value` and optionally a `label`,
         * `tooltip`, `qualifier`, `icon`, `iconRight` and `disabled`, which are passed to ButtonV2.
         */
        items: {type: Array, default: () => []},
        /**
         * The selected value(s), bound with v-model. A single item's value in single select mode
         * (`null` when nothing is selected), or an array of values when `multiple` is set.
         */
        value: {type: [String, Number, Boolean, Array, Object], default: null},
        /**
         * Lets more than one item be selected. The value is then an array of the selected values.
         */
        multiple: {type: Boolean, default: false},
        /**
         * Lets the user deselect the last selected item, leaving nothing selected.
         */
        allowEmpty: {type: Boolean, default: false},
        /**
         * Size of the buttons. One of BUTTON_SIZES (xs, s, m, l).
         */
        size: {type: String, default: () => MeadCommon.BUTTON_SIZES.MEDIUM},
        /**
         * Color of the selected buttons: a COLOR_STYLES value (e.g. "primary"), a tint of one
         * (e.g. "primary-20") or any CSS color (e.g. "#BAC3C4").
         */
        color: {type: String, default: () => MeadCommon.BUTTON_COLOR_STYLES.PRIMARY},
        /**
         * Color of the group background and the unselected buttons: a COLOR_STYLES value
         * (e.g. "default"), a tint of one (e.g. "primary-20") or any CSS color (e.g. "#BAC3C4").
         */
        defaultColor: {type: String, default: () => MeadCommon.BUTTON_COLOR_STYLES.DEFAULT},
        /**
         * Renders the group inline (inline-flex, auto width) instead of full width, where the
         * buttons share the width evenly.
         */
        inline: {type: Boolean, default: true},
        /**
         * Disables every button in the group.
         */
        disabled: {type: Boolean, default: false}
    },
    computed: {
        uiSelectedValues: function () {
            if (this.multiple) return Array.isArray(this.value) ? this.value : [];
            return this.value === null || this.value === undefined ? [] : [this.value];
        },
        uiBackgroundVars: function () {
            return MeadCommon.colorVars(this.defaultColor, {bg: '--button-group-bg', fg: '--button-group-fg'});
        },
        uiHasBackgroundVars: function () {
            return Object.keys(this.uiBackgroundVars).length > 0;
        }
    },
    methods: {
        uiItemKey: function (item, index) {
            return typeof item.value === 'object' ? index : `${typeof item.value}-${item.value}`;
        },
        uiIsSelected: function (item) {
            return this.uiSelectedValues.includes(item.value);
        },
        onItemClick: function (item) {
            const selected = this.uiIsSelected(item);
            //  Keep at least one item selected unless allowEmpty is set
            if (selected && this.uiSelectedValues.length === 1 && this.allowEmpty !== true) return;

            let next;
            if (this.multiple) {
                next = selected
                    ? this.uiSelectedValues.filter(value => value !== item.value)
                    : [...this.uiSelectedValues, item.value];
            } else {
                next = selected ? null : item.value;
            }

            this.$emit('input', next);
            this.$emit('change', next, item);
        }
    }
};
</script>
<style lang="scss">
@import '../../../sass/_variables.scss';
.mead-button-group {
    display: flex;
    width: 100%;
    max-width: 100%;
    gap: $s-xxs;
    padding: $s-xs;
    border-radius: calc(#{$br-m} + #{$s-xxs});
    background-color: var(--c-brand-default-bg);
    color: var(--c-brand-default-fg);

    &.el--inline {
        display: inline-flex;
        width: auto;
    }

    // Set only alongside --button-group-bg/--button-group-fg,
    // so a nested button group never picks up its parent's colors
    &.el--bg {
        background-color: var(--button-group-bg);
        color: var(--button-group-fg, var(--c-txt));
    }

    &:not(.el--inline) > .el-button-group-item {
        flex: 1 1 0;
        min-width: 0;
    }
}
</style>
